/**
 * Feeds the scale store what it needs to dial the cloud relay.
 *
 * Mounted once near the root rather than by each weight field, for two reasons.
 * The credentials are app-wide — one signed-in operator, one vendor — so
 * re-deriving them per field is waste. And `useScale` stays free of the auth
 * context, which matters more than it sounds: that context transitively pulls
 * in modules reading `import.meta`, and anything importing it can't be compiled
 * by the CommonJS output ts-jest emits. Keeping it here means weight fields
 * remain testable.
 *
 * Renders nothing.
 */
import { useContext, useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import { environment } from 'environments/environment';
import { CognitoAuthContext } from 'features/auth';
import { setRelayCredentials } from 'lib/scale';
import { vendorState } from 'state';

/**
 * Keep the scale store's relay credentials current.
 */
export function ScaleRelayCredentials() {
  const cognito = useContext(CognitoAuthContext);
  const vendor = useRecoilValue(vendorState);

  const url = environment.deviceRelayUrl;
  const token = cognito?.idToken ?? '';
  const vendorId = (vendor?._id as string | undefined) ?? '';

  useEffect(() => {
    // Any missing piece — no relay on this stage, signed out, no vendor chosen
    // — disables the tier, and the store falls back to the loopback bridge,
    // WebHID, or typing exactly as it did before the relay existed.
    setRelayCredentials(
      url && token && vendorId ? { url, token, vendorId } : null,
    );
  }, [url, token, vendorId]);

  // Re-pushed whenever the token refreshes, so the next reconnect never
  // presents an expired one. An already-open socket is unaffected: the relay
  // authorizes at connect time only.
  return null;
}
